import {
  Component,
  EventEmitter,
  Output,
  Input,
  OnInit,
  ViewEncapsulation,
  OnDestroy,
} from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { RewardService } from 'src/app/core/api/reward/reward.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { CouponCardService } from '../../../../core/services/coupon-card/coupon-card.service';
import { AuthenticatedUserService } from 'src/app/core/services/authenticated-user/authenticated-user.service';
import { BusinessStatus } from 'src/app/shared/models/constants/business-status';
import { CouponStatus } from 'src/app/shared/models/constants/coupon-status';
import { BusinessFrozenException } from 'src/app/shared/errors/user-management-service/user-management-service-errors';

@Component({
  selector: 'app-coupon-status',
  templateUrl: './coupon-status.component.html',
  styleUrls: ['./coupon-status.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Coupon status popup
 */
export class CouponStatusComponent implements OnInit, OnDestroy {
  @Output() closeClick = new EventEmitter();
  @Output() updateCoupon = new EventEmitter();
  @Input() couponData: any;
  @Input() businessStatus!: BusinessStatus;
  @Input() disabledAt?: string;
  loading: boolean = false;
  selected: 'USED' | 'CANCELED' | '' = '';
  form = new FormGroup({
    date: new FormControl<Date>(new Date(), [Validators.required]),
    invoice: new FormControl<string>('', [Validators.required]),
    discount: new FormControl<number>(null as unknown as number, [
      Validators.required,
      Validators.max(9999999999),
    ]),
    reseller: new FormControl<string>('', [Validators.required]),
    acknowledge: new FormControl<boolean>(false, [Validators.requiredTrue]),
  });
  activePoints = 0;
  expiredPoints = 0;
  isExpiredCoupon = false;
  isRedeemedCoupon = false;
  isSuspendedCoupon = false;
  minDate!: Date;
  maxDate!: Date;
  labels = {
    active: 'active',
    redeemed: 'redeemed',
    canceled: 'canceled',
    couponStatusText: 'coupon-status',
    cancel: 'cancel',
    changeStatus: 'change-status',
    textCancel: 'cancel-coupon-1',
    textCancelBold: 'cancel-coupon-2',
    confirmCancel: 'confirm-cancel',
    formDate: 'redemption-date',
    formInvoice: 'invoice',
    formValue: 'discount-value',
    formReseller: 'reseller',
    activePoints: 'active-points',
    expiredPoints: 'expired-points',
  };
  private subsink = new Subscription();
  /**
   * @constructor
   */
  constructor(
    private translationService: TranslationService,
    private rewardService: RewardService,
    private snackbarService: SnackBarService,
    private couponCardService: CouponCardService,
    private authenticatedUserService: AuthenticatedUserService
  ) {}

  /**
   * onInit
   */
  ngOnInit(): void {
    this.translationService.translateObject(this.labels);
    const id = this.couponData.couponId;
    this.isExpiredCoupon = this.couponData.status === CouponStatus.EXPIRED;
    this.isRedeemedCoupon = this.couponData.status === CouponStatus.REDEEMED;
    this.isSuspendedCoupon = this.checkIsSuspended();
    this.couponCardService.couponStatuses.forEach((item) => {
      if (item.couponId === id) {
        this.selectChip(item.selected);
      }
    });

    const couponStatusInfo = this.couponCardService.findCouponStatusById(
      this.couponData.couponId
    );

    if (couponStatusInfo) {
      this.form.patchValue({
        date: couponStatusInfo.date || new Date(),
        invoice: couponStatusInfo.invoice,
        discount: couponStatusInfo.discount,
        reseller: couponStatusInfo.reseller,
        acknowledge: couponStatusInfo.acknowledge,
      });
    }

    if (this.isRedeemedCoupon) {
      this.form.patchValue({
        date: new Date(this.couponData.reclaimedDate) || new Date(),
        invoice: this.couponData.invoice,
        discount: this.couponData.discountValue,
        reseller: this.couponData.resellerIdentification,
        acknowledge: this.couponData.acknowledge,
      });
      this.selectChip('USED');
    } else {
      this.form.patchValue({
        date: new Date(this.couponData.reclaimedDate),
      });
    }

    this.onFormValueChanges('date');
    this.onFormValueChanges('invoice');
    this.onFormValueChanges('discount');
    this.onFormValueChanges('reseller');
    this.onFormValueChanges('acknowledge');

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const currentDay = new Date().getDate();

    this.setDateRange(currentYear, currentMonth, currentDay);
  }

  /**
   * indicates if the coupon is actually suspended
   * @return {boolean}
   */
  checkIsSuspended(): boolean {
    return (
      this.couponData.status === CouponStatus.ACTIVE &&
      this.businessStatus === BusinessStatus.DISABLED
    );
  }

  /**
   * sets the maximum and minimum dates for the datepicker
   * @param {number} currentYear
   * @param {number} currentMonth
   * @param {number} currentDay
   */
  setDateRange(currentYear: number, currentMonth: number, currentDay: number) {
    if (this.isExpiredCoupon) {
      this.minDate = new Date(this.couponData.reclaimedDate);

      this.maxDate = new Date(this.couponData.expirationDate);
      this.maxDate.setDate(this.maxDate.getDate() + 30);
    } else {
      this.minDate = new Date(currentYear - 20, currentMonth, currentDay);
      this.maxDate = new Date(currentYear, currentMonth, currentDay);
    }
  }

  /**
   * Check any form value change and set it to coupon statuses array
   * @param {string} name
   */
  private onFormValueChanges(
    name: 'date' | 'invoice' | 'discount' | 'reseller' | 'acknowledge'
  ) {
    this.form.get(name)!.valueChanges.subscribe((item) => {
      const couponStatusInfo = this.couponCardService.findCouponStatusById(
        this.couponData.couponId
      );

      couponStatusInfo[name] = item as any;

      this.couponCardService.removeCouponStatusById(couponStatusInfo?.couponId);
      this.couponCardService.couponStatuses.push(couponStatusInfo);
    });
  }

  /**
   * Closes popup
   */
  clickClose() {
    // Reset form
    if (!this.isRedeemedCoupon) {
      this.selected = '';
    }
    this.closeClick.emit();
  }

  /**
   * Submit change status
   */
  changeStatus() {
    this.loading = true;

    if (this.isRedeemedCoupon) {
      this.rewardService
        .updateCouponRedeemedInfo(
          this.couponData.couponId,
          this.form.controls.invoice.value!,
          this.form.controls.reseller.value!,
          this.form.controls.discount.value!,
          this.form.controls.date.value!
        )
        .subscribe({
          next: (coupon) => this.onCouponSuccess(coupon),
          error: (err) => this.onCouponError(err),
        });
      return;
    }

    if (this.selected === 'USED') {
      this.rewardService
        .setCouponRedeemed(
          this.couponData.couponId,
          this.form.controls.invoice.value!,
          this.form.controls.reseller.value!,
          this.form.controls.discount.value!,
          this.form.controls.date.value!
        )
        .subscribe({
          next: (coupon) => {
            this.updateCoupon.emit({
              ...coupon,
              originalDate: this.couponData.reclaimedDate,
            });
            this.isRedeemedCoupon = this.couponData.status === 'USED';
            this.couponData = coupon;
            this.loading = false;
            this.translationService
              .translateString('coupon-status-success')
              .then((string) => this.snackbarService.success(string));
          },
          error: (err) => this.onCouponError(err),
        });
    }
    if (this.selected === 'CANCELED') {
      this.rewardService.setCouponCanceled(this.couponData.couponId).subscribe({
        next: (coupon) => this.onCouponSuccess(coupon),
        error: (err) => this.onCouponError(err),
      });
    }
  }

  /**
   * Handles successful coupon status update
   * @param {any} coupon updated coupon data
   */
  private onCouponSuccess(coupon: any): void {
    this.updateCoupon.emit({
      ...coupon,
      originalDate: this.couponData.reclaimedDate,
    });
    this.couponData = coupon;
    this.loading = false;
    this.translationService
      .translateString('coupon-status-success')
      .then((string) => this.snackbarService.success(string));
  }

  /**
   * Handles error from coupon status update
   * @param {any} err error response from the API
   */
  private onCouponError(err?: any): void {
    this.loading = false;
    if (err instanceof BusinessFrozenException) {
      this.translationService
        .translateString('business-is-frozen-exception')
        .then((string) => this.snackbarService.error(string));
      return;
    }
    if (err?.error?.errorMessage) {
      this.snackbarService.error(err.error.errorMessage);
      return;
    }
    this.translationService
      .translateString('coupon-status-error')
      .then((string) => this.snackbarService.error(string));
  }

  /**
   * Sets selected
   * @param {string} form
   */
  selectChip(form: 'USED' | 'CANCELED' | '') {
    if (form !== '') {
      this.selected = form;
      this.form.controls.acknowledge.setValue(false);

      const couponStatus = this.couponCardService.findCouponStatusById(
        this.couponData.couponId
      );

      if (couponStatus) {
        couponStatus.selected = form;
      }
    }
  }

  /**
   * teste
   * @return {boolean}
   */
  isShowCancel(): boolean {
    return (
      !this.isExpiredCoupon && !this.isRedeemedCoupon && !this.isSuspendedCoupon
    );
  }

  /**
   * Verifies if is disabled
   * @return {boolean}
   */
  disabled(): boolean {
    return this.selected === '' || this.selected === 'USED'
      ? !this.form.valid
      : !this.form.controls.acknowledge.valid;
  }

  /**
   * @return {string}
   */
  get separator(): string {
    const adminRegionName = this.authenticatedUserService.currentRegion?.name;
    return adminRegionName === 'Brazil' ? '.' : ',';
  }

  /**
   * Clean up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }
}
